type ReminderStorage = {
  getItem: (key: string) => Promise<string | null>;
  setItem: (key: string, value: string) => Promise<unknown>;
  removeItem: (key: string) => Promise<unknown>;
};

type OfferedReminder = { owner: string; offered: true };

const STORAGE_KEY = 'gamma.wallet-security-reminder.v1';

const isOfferedReminder = (value: unknown): value is OfferedReminder =>
  typeof value === 'object' &&
  value !== null &&
  'owner' in value &&
  typeof value.owner === 'string' &&
  'offered' in value &&
  value.offered === true;

const parseOfferedOwner = (stored: string | null): string | null => {
  try {
    const parsed: unknown = JSON.parse(stored ?? 'null');
    return isOfferedReminder(parsed) ? parsed.owner : null;
  } catch {
    return null;
  }
};

export class WalletSecurityReminder {
  private generation = 0;
  private offeredOwner: string | null = null;
  private queue: Promise<unknown> = Promise.resolve();

  constructor(private readonly storage: ReminderStorage) {}

  take(owner: string, hasTotp: boolean): Promise<boolean> {
    const generation = this.generation;
    return this.serial(() => this.offerOnce(owner, hasTotp, generation));
  }

  reset(): Promise<void> {
    this.generation += 1;
    this.offeredOwner = null;
    return this.serial(() => this.forgetOfferBestEffort());
  }

  private async offerOnce(owner: string, hasTotp: boolean, generation: number): Promise<boolean> {
    if (!owner || hasTotp || generation !== this.generation) {
      return false;
    }

    const offered = this.offeredOwner === owner || (await this.readOfferedOwner()) === owner;

    if (generation !== this.generation) {
      return false;
    }

    this.offeredOwner = owner;

    if (offered) {
      return false;
    }

    await this.rememberOfferBestEffort(owner);
    return generation === this.generation;
  }

  private readOfferedOwner(): Promise<string | null> {
    return this.storage.getItem(STORAGE_KEY).then(parseOfferedOwner, () => null);
  }

  private async rememberOfferBestEffort(owner: string): Promise<void> {
    const reminder: OfferedReminder = { owner, offered: true };
    await this.storage.setItem(STORAGE_KEY, JSON.stringify(reminder)).catch(() => undefined);
  }

  private async forgetOfferBestEffort(): Promise<void> {
    await this.storage.removeItem(STORAGE_KEY).catch(() => undefined);
  }

  private serial<T>(operation: () => Promise<T>): Promise<T> {
    const result = this.queue.then(operation, operation);
    this.queue = result.catch(() => undefined);
    return result;
  }
}
